import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Paper, useTheme, useMediaQuery } from '@mui/material';
import { 
  ChevronLeft, 
  ChevronRight,
  Dashboard,
  GpsFixed,
  Phone,
  Schedule,
  LocationOn,
  TrendingUp
} from '@mui/icons-material';
import PlayStoreBtn from './PlayStoreBtn';

const FeatureSlideshow = ({ onSlideChange }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      title: "AI-Powered Dashboard",
      description: "Transform your sales strategy with intelligent insights. See exactly where your opportunities lie and make data-driven decisions that drive results.",
      icon: Dashboard,
      color: "#8B5CF6",
      benefit: "Make smarter decisions faster"
    },
    {
      title: "Smart Lead Management",
      description: "Never miss a hot prospect again. Our AI automatically identifies and prioritizes your highest-value leads, so you focus on what matters most.",
      icon: GpsFixed,
      color: "#10B981",
      benefit: "Focus on quality prospects"
    },
    {
      title: "Call Intelligence",
      description: "Understand every conversation better. AI analyzes your calls to reveal buyer sentiment, key interests, and the perfect follow-up strategy.",
      icon: Phone,
      color: "#06B6D4",
      benefit: "Close more deals with insights"
    },
    {
      title: "Automated Scheduling",
      description: "Your time is valuable. Let AI handle the scheduling complexity while you focus on building relationships and closing deals.",
      icon: Schedule,
      color: "#F59E0B",
      benefit: "Reclaim your time for selling"
    },
    {
      title: "Site Visit Optimization",
      description: "Maximize every site visit with intelligent routing and scheduling. Show properties that match client preferences and optimize your travel time.",
      icon: LocationOn,
      color: "#14B8A6",
      benefit: "Show the right properties faster"
    },
    {
      title: "Performance Analytics",
      description: "Track what works and replicate success. Get clear visibility into your performance patterns and discover new ways to excel.",
      icon: TrendingUp,
      color: "#6366F1",
      benefit: "Accelerate your growth"
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      const newSlide = (currentSlide + 1) % features.length;
      setCurrentSlide(newSlide);
      onSlideChange?.(newSlide, features.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [features.length, currentSlide, onSlideChange]);

  const nextSlide = () => {
    const newSlide = (currentSlide + 1) % features.length;
    setCurrentSlide(newSlide);
    onSlideChange?.(newSlide, features.length);
  };

  const prevSlide = () => {
    const newSlide = (currentSlide - 1 + features.length) % features.length;
    setCurrentSlide(newSlide);
    onSlideChange?.(newSlide, features.length);
  };

  const handleSlideClick = (index) => {
    setCurrentSlide(index);
    onSlideChange?.(index, features.length);
  };

  if (isMobile) {
    return null; // Hide on mobile to save space
  }

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 4,
        background: 'linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 50%, #F8FAFC 100%)',
        color: '#1E293B',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(139, 92, 246, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.05) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(6, 182, 212, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 60% 60%, rgba(245, 158, 11, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 30% 30%, rgba(20, 184, 166, 0.02) 0%, transparent 50%),
            radial-gradient(circle at 70% 70%, rgba(99, 102, 241, 0.02) 0%, transparent 50%)
          `,
          zIndex: 1
        }}
      />

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center', maxWidth: '500px' }}>
        {/* Slide Content */}

        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: `linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 50%, rgba(255, 255, 255, 0.90) 100%)`,
            backdropFilter: 'blur(20px)',
            borderRadius: 3,
            color: '#1E293B',
            border: `2px solid ${features[currentSlide].color}20`,
            position: 'relative',
            overflow: 'hidden',
            transform: 'translateY(0) scale(1)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            cursor: 'pointer',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `linear-gradient(135deg, ${features[currentSlide].color}08 0%, ${features[currentSlide].color}15 100%)`,
              opacity: 0,
              transition: 'opacity 0.3s ease',
              zIndex: 1
            },
            '&:hover': {
              transform: 'translateY(-8px) scale(1.02)',
              boxShadow: `0 20px 40px ${features[currentSlide].color}25, 0 8px 16px rgba(0, 0, 0, 0.1)`,
              border: `2px solid ${features[currentSlide].color}40`,
              '&::before': {
                opacity: 1
              },
              '& .feature-icon': {
                transform: 'scale(1.05)',
                boxShadow: `
                  0 10px 15px -3px rgba(0, 0, 0, 0.1),
                  0 4px 6px -2px rgba(0, 0, 0, 0.05),
                  0 0 0 1px ${features[currentSlide].color}30
                `,
                background: `linear-gradient(135deg, ${features[currentSlide].color}18 0%, ${features[currentSlide].color}25 50%, ${features[currentSlide].color}22 100%)`
              },
              '& .feature-title': {
                transform: 'translateY(-2px)',
                textShadow: `0 2px 4px ${features[currentSlide].color}30`
              },
              '& .benefit-badge': {
                transform: 'scale(1.05)',
                boxShadow: `0 4px 12px ${features[currentSlide].color}30`
              }
            }
          }}
        >
          {/* Animated Background Pattern */}
          <Box sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 100,
            height: 100,
            background: `radial-gradient(circle, ${features[currentSlide].color}20 0%, transparent 70%)`,
            borderRadius: '50%',
            opacity: 0.6,
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
              '50%': { transform: 'translateY(-20px) rotate(180deg)' }
            }
          }} />

          <Box sx={{ position: 'relative', zIndex: 2 }}>
            {/* Icon with Animation */}
            <Box 
              className="feature-icon"
              sx={{ 
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '70px',
                height: '70px',
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${features[currentSlide].color}12 0%, ${features[currentSlide].color}20 50%, ${features[currentSlide].color}15 100%)`,
                boxShadow: `
                  0 4px 6px -1px rgba(0, 0, 0, 0.1),
                  0 2px 4px -1px rgba(0, 0, 0, 0.06),
                  0 0 0 1px ${features[currentSlide].color}20
                `,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                mx: 'auto', // Center horizontally
                '& .MuiSvgIcon-root': {
                  fontSize: '2rem',
                  color: features[currentSlide].color,
                  filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                }
              }}
            >
              {React.createElement(features[currentSlide].icon, {
                sx: {
                  fontSize: '2rem',
                  color: features[currentSlide].color,
                  filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))'
                }
              })}
            </Box>
            
            {/* Title with Animation */}
            <Typography
              className="feature-title"
              variant="h4"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
                color: features[currentSlide].color,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                background: `linear-gradient(135deg, ${features[currentSlide].color} 0%, ${features[currentSlide].color}CC 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {features[currentSlide].title}
            </Typography>
            
            {/* Description */}
            <Typography
              variant="h6"
              sx={{
                color: '#64748B',
                lineHeight: 1.6,
                fontWeight: 400,
                mb: 3,
                transition: 'color 0.3s ease'
              }}
            >
              {features[currentSlide].description}
            </Typography>

            {/* Enhanced Benefit Badge */}
            <Box 
              className="benefit-badge"
              sx={{
                display: 'inline-block',
                background: `linear-gradient(135deg, ${features[currentSlide].color}12 0%, ${features[currentSlide].color}18 50%, ${features[currentSlide].color}15 100%)`,
                color: features[currentSlide].color,
                px: 3,
                py: 1.5,
                borderRadius: '25px',
                fontSize: '0.875rem',
                fontWeight: 700,
                border: `2px solid ${features[currentSlide].color}30`,
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '-100%',
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(90deg, transparent, ${features[currentSlide].color}20, transparent)`,
                  transition: 'left 0.5s ease',
                },
                '&:hover::before': {
                  left: '100%'
                }
              }}
            >
              ✨ {features[currentSlide].benefit}
            </Box>
          </Box>
        </Paper>


        {/* Enhanced Navigation Dots */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mb: 3 }}>
          {features.map((feature, index) => (
            <Box
              key={index}
              onClick={() => handleSlideClick(index)}
              sx={{
                width: index === currentSlide ? 24 : 12,
                height: 12,
                borderRadius: '6px',
                backgroundColor: index === currentSlide ? feature.color : '#CBD5E1',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: index === currentSlide 
                    ? `linear-gradient(90deg, ${feature.color}80, ${feature.color})`
                    : 'transparent',
                  borderRadius: '6px',
                  opacity: 0,
                  transition: 'opacity 0.3s ease'
                },
                '&:hover': {
                  backgroundColor: index === currentSlide ? feature.color : '#94A3B8',
                  transform: 'scale(1.3)',
                  boxShadow: `0 4px 12px ${feature.color}40`,
                  '&::before': {
                    opacity: 1
                  }
                }
              }}
            />
          ))}
        </Box>

        {/* Play Store Button */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="body2" sx={{ mb: 2, color: '#64748B', fontWeight: 500 }}>
            Download our mobile app for on-the-go access
          </Typography>
          <PlayStoreBtn size="medium" />
        </Box>
      </Box>
    </Box>
  );
};

export default FeatureSlideshow;
